import { useState, useEffect } from 'react';
import { HiCash, HiShoppingBag, HiUsers, HiTrendingUp } from 'react-icons/hi';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,                              // ✅ added to silence warning
} from 'chart.js';
import axios from 'axios';             // ✅ default axios (has interceptor)

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler);

const API =  process.env.REACT_APP_API_URL;   // backend base URL

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [ordersByStatus, setOrdersByStatus] = useState({});
  const [visitsData, setVisitsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // All requests go through default axios → interceptor adds the token
        const [statsRes, revenueRes, orderStatsRes, visitsRes] = await Promise.all([
          axios.get(`${API}/api/admin/stats`),
          axios.get(`${API}/api/admin/revenue-daily`),
          axios.get(`${API}/api/admin/orders/stats`),
          axios.get(`${API}/api/admin/visits/daily`),
        ]);

        setStats(statsRes.data);
        setRevenueData(revenueRes.data);
        setOrdersByStatus(orderStatsRes.data);
        setVisitsData(visitsRes.data);
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
        setError('Could not load dashboard. Please make sure you are logged in as an admin.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <div className="text-center py-10 text-gray-500">Loading dashboard…</div>;
  }

  if (error || !stats) {
    return <div className="text-center py-10 text-gray-500">{error || 'Unable to load dashboard data.'}</div>;
  }

  const revenueChartData = {
    labels: revenueData.map(d => d.date),
    datasets: [
      {
        label: 'Revenue (₦)',
        data: revenueData.map(d => d.revenue),
        borderColor: '#000',
        backgroundColor: 'rgba(0,0,0,0.1)',
        tension: 0.3,
        fill: true,
      },
    ],
  };

  const statusChartData = {
    labels: Object.keys(ordersByStatus),
    datasets: [
      {
        label: 'Orders',
        data: Object.values(ordersByStatus),
        backgroundColor: ['#000', '#666', '#999', '#aaa'],
      },
    ],
  };

  const visitsChartData = {
    labels: visitsData.map(d => d.date),
    datasets: [
      {
        label: 'Visits',
        data: visitsData.map(d => d.visits),
        borderColor: '#666',
        backgroundColor: 'rgba(0,0,0,0.05)',
        tension: 0.3,
        fill: true,
      },
    ],
  };

  return (
    <div>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={<HiTrendingUp className="text-2xl" />}
          label="Total Revenue"
          value={`₦${stats.totalRevenue?.toLocaleString()}`}
        />
        <StatCard
          icon={<HiShoppingBag className="text-2xl" />}
          label="Total Orders"
          value={stats.totalOrders}
        />
        <StatCard
          icon={<HiUsers className="text-2xl" />}
          label="Total Customers"
          value={stats.totalCustomers}
        />
        <StatCard
          icon={<HiCash className="text-2xl" />}
          label="Avg. Order Value"
          value={
            stats.totalOrders > 0
              ? `₦${(stats.totalRevenue / stats.totalOrders).toFixed(2)}`
              : '₦0'
          }
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <h3 className="font-semibold mb-4">Revenue Over Time</h3>
          {revenueData.length > 0 ? (
            <Line data={revenueChartData} options={{ responsive: true }} />
          ) : (
            <p className="text-gray-400">No revenue data yet</p>
          )}
        </div>

        {/* Orders by Status */}
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <h3 className="font-semibold mb-4">Orders by Status</h3>
          {Object.keys(ordersByStatus).length > 0 ? (
            <Bar data={statusChartData} options={{ responsive: true }} />
          ) : (
            <p className="text-gray-400">No orders yet</p>
          )}
        </div>

        {/* Visits Chart */}
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <h3 className="font-semibold mb-4">Daily Visits (Last 30 Days)</h3>
          {visitsData.length > 0 ? (
            <Line data={visitsChartData} options={{ responsive: true }} />
          ) : (
            <p className="text-gray-400">No visit data yet</p>
          )}
        </div>

        {/* Additional card */}
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <h3 className="font-semibold mb-4">Quick Stats</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500">Paid Orders</span>
              <span className="font-medium">{stats.paidOrders}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Pending Orders</span>
              <span className="font-medium">{stats.pendingOrders}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Total Products</span>
              <span className="font-medium">{stats.totalProducts}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <div className="bg-white p-4 rounded-lg border border-gray-200 flex items-center gap-4">
      <div className="text-gray-600">{icon}</div>
      <div>
        <p className="text-sm text-gray-500">{label}</p>
        <p className="text-xl font-bold">{value}</p>
      </div>
    </div>
  );
}